import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  LineController,
  BarController,
  type ChartOptions,
  type ChartData,
  type ScriptableContext
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  LineController,
  BarController
);

interface DatasetConfig {
  data: number[];
  type?: 'line' | 'bar';
  label?: string;
  color?: string;
  fillColor?: string;
  borderWidth?: number;
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  pointRadius?: number;
  tension?: number;
  borderColor?: string;
}

interface AreaChartProps {
  data?: number[];
  datasets?: DatasetConfig[];
  labels?: string[];
  color?: string;
  height?: number;
  tension?: number;
  fillColor?: string;
  scalesx?: boolean;
  scalesy?: boolean;
}

const AreaChart: React.FC<AreaChartProps> = ({
  data,
  datasets,
  labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  color = '#0d6efd',
  height = 100,
  tension,
  fillColor,
  scalesx = false,
  scalesy = false,
}) => {
  // Use datasets if provided, otherwise fall back to single data array
  const chartDatasets = datasets ? datasets.map((dataset, index) => ({
    type: dataset.type || 'line',
    fill: true,
    label: dataset.label || `Dataset ${index + 1}`,
    data: dataset.data,
    backgroundColor: (context: ScriptableContext<'line' | 'bar'>) => {
      const ctx = context.chart.ctx;
      const gradient = ctx.createLinearGradient(0, 0, 0, (height - 20));
      const datasetColor = dataset.color || color;
      const datasetFillColor = dataset.fillColor;
      gradient.addColorStop(0, datasetFillColor || `${datasetColor}99`);
      gradient.addColorStop(1, `${datasetColor}00`);
      return gradient;
    },
    borderColor: dataset.borderColor || color,
    tension: dataset.tension || tension,
    pointRadius: dataset.pointRadius !== undefined ? dataset.pointRadius : 0,
    pointHoverRadius: 4,
    borderWidth: dataset.borderWidth || 1,
    pointBackgroundColor: dataset.pointBackgroundColor,
    pointBorderColor: dataset.pointBorderColor,
  })) : [
    {
      type: 'line' as const,
      fill: true,
      label: 'Data',
      data: data || [10, 20, 15, 25, 18, 30, 22],
      borderColor: color,
      backgroundColor: (context: ScriptableContext<'line'>) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, (height - 10));
        gradient.addColorStop(0, fillColor || `${color}99`);
        gradient.addColorStop(1, `${color}00`);
        return gradient;
      },
      tension: tension,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 1,
    },
  ];

  const chartData: ChartData<'line' | 'bar'> = {
    labels,
    datasets: chartDatasets as any,
  };

  const options: ChartOptions<'line' | 'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        display: scalesx,
        grid: {
          display: false,
        },
      },
      y: {
        display: scalesy,
        grid: {
          display: false,
        },
        min: 0,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return <div style={{ height: height, width: '100%' }}><Chart type='line' options={options} data={chartData} /></div>;
};

export default AreaChart;
